/*
 * Private saved views (master-inbox plan, step 3b): the list on screen (its search, filters and
 * order) kept under a name, listed under the label rows in the rail and seen by nobody else
 * (`/inbox/views`). "Save view" names a new one, or keeps the changes made to the view on screen;
 * a view's row in the rail renames or deletes it through the incumbent option menu. The API holds
 * the limits (20 views, names up to 80 characters); the page says them before it asks.
 */
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { MoreIcon } from "../icons.tsx";
import { OptionMenu } from "../option-menu.tsx";
import type { NavRow } from "../shell-nav.ts";
import { InboxApiError, inboxApi, type ViewQuery } from "./api.ts";

const VIEW_LIMIT = 20;
const NAME_MAX = 80;

function failure(error: unknown): string {
  if (error instanceof InboxApiError && error.code === "view_limit")
    return `You have ${VIEW_LIMIT} saved views, the most there can be. Delete one in the sidebar to save another.`;
  return "The view could not be saved. Try again.";
}

/** The writes, each followed by a fresh list of views for the rail and the page. */
function useViewWrites(workspaceId: string) {
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: ["inbox-v2", workspaceId, "views"] });
  return {
    create: async (name: string, query: ViewQuery) => {
      const view = await inboxApi.createView(workspaceId, name, query);
      await refresh();
      return view;
    },
    update: async (id: string, change: { name?: string; query?: ViewQuery }) => {
      await inboxApi.updateView(workspaceId, id, change);
      await refresh();
    },
    remove: async (id: string) => {
      await inboxApi.deleteView(workspaceId, id);
      await refresh();
    },
  };
}

/** Closes on a press outside `root`, as the option menu does. */
function useOutside(root: React.RefObject<HTMLElement | null>, open: boolean, close: () => void) {
  const latest = useRef(close);
  latest.current = close;
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      latest.current();
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open, root]);
}

function NameForm(props: {
  initial: string;
  submit: string;
  onSave: (name: string) => Promise<void>;
  onCancel: () => void;
  hint?: ReactNode;
}) {
  const id = useId();
  const [name, setName] = useState(props.initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return setError("Give the view a name.");
    setBusy(true);
    setError(null);
    try {
      await props.onSave(trimmed);
    } catch (caught) {
      setError(failure(caught));
      setBusy(false);
    }
  };
  return (
    <form className="lcr-view-form" onSubmit={(event) => void save(event)}>
      <label className="lcr-settings-field-label" htmlFor={id}>
        Name
      </label>
      <input
        id={id}
        type="text"
        // biome-ignore lint/a11y/noAutofocus: the form opens in place of the button that asked for it.
        autoFocus
        maxLength={NAME_MAX}
        value={name}
        aria-invalid={error !== null || undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => setName(event.target.value)}
      />
      {error && (
        <p className="lcr-view-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
      {props.hint}
      <span className="lcr-view-form-actions">
        <button type="button" className="lcr-small-button lcr-ghost" onClick={props.onCancel}>
          Cancel
        </button>
        <button type="submit" className="lcr-small-button" disabled={busy}>
          {props.submit}
        </button>
      </span>
    </form>
  );
}

/**
 * The heading's "Save view": shown while the list is searched or filtered, or while the view on
 * screen has changes. On a saved view with changes it offers to keep them in that view, or to save
 * them as a new one.
 */
export function SaveView(props: {
  workspaceId: string;
  query: ViewQuery;
  suggestedName: string;
  /** The saved view on screen, when it has changes. */
  changed: { id: string; name: string } | null;
  saved: number;
  onSaved: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const writes = useViewWrites(props.workspaceId);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  useOutside(root, open, () => setOpen(false));
  const full = props.saved >= VIEW_LIMIT;
  return (
    <span className="lcr-save-view" ref={root}>
      <button
        ref={button}
        type="button"
        className="lcr-track-more"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {props.changed ? "Save changes" : "Save view"}
      </button>
      {open && (
        <div
          className="lcr-popover lcr-save-view-popover"
          role="dialog"
          aria-label={props.changed ? "Save changes" : "Save view"}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            event.stopPropagation();
            close();
          }}
        >
          {props.changed && (
            <SaveChanges
              name={props.changed.name}
              onSave={async () => {
                await writes.update(props.changed?.id ?? "", { query: props.query });
                close();
              }}
            />
          )}
          <p className="lcr-label">{props.changed ? "Or save as a new view" : "Save view"}</p>
          {full ? (
            <p className="lcr-view-note">
              You have {VIEW_LIMIT} saved views, the most there can be. Delete one in the sidebar to
              save another.
            </p>
          ) : (
            <NameForm
              initial={props.suggestedName.slice(0, NAME_MAX)}
              submit="Save view"
              hint={
                <p className="lcr-view-note">
                  Keeps this search, its filters and its order under Views. Only you see it.
                </p>
              }
              onCancel={close}
              onSave={async (name) => {
                const view = await writes.create(name, props.query);
                setOpen(false);
                props.onSaved(view.id);
              }}
            />
          )}
        </div>
      )}
    </span>
  );
}

function SaveChanges(props: { name: string; onSave: () => Promise<void> }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="lcr-view-update">
      <button
        type="button"
        className="lcr-small-button"
        onClick={() => void props.onSave().catch(() => setFailed(true))}
      >
        Update “{props.name}”
      </button>
      {failed && (
        <p className="lcr-view-error" role="alert">
          The view could not be updated. Try again.
        </p>
      )}
    </div>
  );
}

type RowMode = "idle" | "renaming" | "deleting";

/**
 * A saved view's row in the rail, with its Rename and Delete behind the option menu. Renaming swaps
 * the row for its name field; deleting asks once, in the row, before the view is gone.
 */
export function ViewRow(props: {
  workspaceId: string;
  row: NavRow;
  link: ReactNode;
  /** Called after the view on screen was deleted, to leave it. */
  onDeletedCurrent: () => void;
}) {
  const [mode, setMode] = useState<RowMode>("idle");
  const writes = useViewWrites(props.workspaceId);
  const id = props.row.viewId ?? "";
  if (mode === "renaming")
    return (
      <div className="lcr-rail-view" data-mode="renaming">
        <NameForm
          initial={props.row.label}
          submit="Rename"
          onCancel={() => setMode("idle")}
          onSave={async (name) => {
            await writes.update(id, { name });
            setMode("idle");
          }}
        />
      </div>
    );
  return (
    <div className="lcr-rail-view" data-mode={mode === "deleting" ? "deleting" : undefined}>
      {props.link}
      <OptionMenu<"rename" | "delete">
        label={`Actions for ${props.row.label}`}
        icon={<MoreIcon size={16} />}
        className="lcr-rail-view-menu"
        value=""
        actions
        caret={false}
        display={null}
        options={[
          { value: "rename", label: "Rename" },
          { value: "delete", label: "Delete" },
        ]}
        onChange={(action) => setMode(action === "rename" ? "renaming" : "deleting")}
      />
      {mode === "deleting" && (
        <DeleteConfirm
          name={props.row.label}
          onCancel={() => setMode("idle")}
          onDelete={async () => {
            await writes.remove(id);
            if (props.row.current) props.onDeletedCurrent();
          }}
        />
      )}
    </div>
  );
}

function DeleteConfirm(props: {
  name: string;
  onCancel: () => void;
  onDelete: () => Promise<void>;
}) {
  const [failed, setFailed] = useState(false);
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => first.current?.focus(), []);
  return (
    // biome-ignore lint/a11y/useSemanticElements: a short question with its two answers, not a form.
    <div className="lcr-view-confirm" role="group" aria-label={`Delete ${props.name}?`}>
      <p className="lcr-view-note">Delete “{props.name}”? The replies stay; only the view goes.</p>
      {failed && (
        <p className="lcr-view-error" role="alert">
          The view could not be deleted. Try again.
        </p>
      )}
      <span className="lcr-view-form-actions">
        <button
          ref={first}
          type="button"
          className="lcr-small-button lcr-ghost"
          onClick={props.onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="lcr-small-button"
          onClick={() => void props.onDelete().catch(() => setFailed(true))}
        >
          Delete
        </button>
      </span>
    </div>
  );
}
